/**
 * Google reCAPTCHA v3 — script loading and token minting.
 *
 * v3 is invisible: no checkbox, no image grid. It watches interaction
 * signals and returns a token the backend exchanges with Google for a
 * 0.0-1.0 "is this a human" score.
 *
 * Loading is lazy and on-demand rather than a <script> tag in
 * index.html. The site is mostly marketing pages with no form on them,
 * and reCAPTCHA is ~250KB of third-party JS that also starts profiling
 * the visitor the moment it loads. Paying that on /pricing to protect
 * /contact is the wrong trade for both performance and privacy.
 *
 * Tokens expire after 2 minutes and are single-use, so they're minted at
 * submit time, never at mount.
 */

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = 'recaptcha-v3';
const LOAD_TIMEOUT_MS = 10_000;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

/** In-flight or completed load, so concurrent forms share one fetch. */
let loadPromise: Promise<void> | null = null;

export function isRecaptchaConfigured(): boolean {
  return Boolean(SITE_KEY);
}

function loadScript(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    if (!SITE_KEY) {
      reject(new Error('REACT_APP_RECAPTCHA_SITE_KEY is not set'));
      return;
    }

    if (window.grecaptcha) {
      resolve();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;

    // A blocked script (ad blocker, offline, corporate proxy) otherwise
    // leaves the submit button spinning forever. Time out and let the
    // caller decide — see submitLead, which submits without a token and
    // lets the server apply its own policy.
    const timer = window.setTimeout(
      () => reject(new Error('reCAPTCHA load timed out')),
      LOAD_TIMEOUT_MS
    );

    script.addEventListener('load', () => {
      window.clearTimeout(timer);
      resolve();
    });
    script.addEventListener('error', () => {
      window.clearTimeout(timer);
      reject(new Error('reCAPTCHA failed to load'));
    });

    document.head.appendChild(script);
  }).catch((error) => {
    // Reset so a later submit can retry rather than inheriting a
    // permanently rejected promise from one bad network moment.
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

/**
 * Start fetching the reCAPTCHA script without minting a token.
 *
 * Called when a visitor first interacts with a lead form. Without this,
 * the ~250KB script downloads on submit, adding roughly half a second
 * to the one click where latency is most visible. Warming on first
 * focus hides that behind the time someone spends typing, while still
 * keeping the script off every page that has no form on it.
 *
 * Safe to call repeatedly — loads are deduplicated. Never throws.
 */
export function warmUpRecaptcha(): void {
  if (!SITE_KEY) return;
  loadScript().catch(() => {
    // Warming is opportunistic; executeRecaptcha() retries and handles
    // the failure path properly.
  });
}

/**
 * Mint a token for an action, or null if reCAPTCHA is unavailable.
 *
 * Never throws: a captcha problem must not be what stops a genuine
 * prospect from contacting us. The backend decides what a missing token
 * means (see RECAPTCHA_FAIL_OPEN).
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  if (!SITE_KEY) return null;

  try {
    await loadScript();
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return null;

    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(SITE_KEY, { action });
  } catch (error) {
    console.warn('[recaptcha] token unavailable:', error);
    return null;
  }
}
